import React, { useEffect, useRef } from "react";
import { Image, Text, TouchableOpacity, View } from "react-native";

import professorSwanImage from "../../assets/professor_swan_transparent.png";
import useReadAloud from "../lessonPlans/present/useReadAloud";
import {
  getInstructionalFlowModes,
  getInstructionalModeMeta,
} from "./journeyInstructionalModes";

const MODE_COPY = {
  I_DO: {
    label: "I DO",
    title: "Professor Swan teaches",
    body: "First I’ll model the idea or procedure with a short example. Watch how I think, not just what I say.",
  },
  WE_DO: {
    label: "WE DO",
    title: "Work with Professor Swan",
    body: "Now we’ll do the next part together. You handle the materials and choices while I coach one step at a time.",
  },
  YOU_DO: {
    label: "YOU DO",
    title: "Your turn",
    body: "Use your own evidence. If you get stuck, ask Professor Swan for a hint—not the answer.",
  },
};

const normalizeMode = (value) => String(value || "").trim().toUpperCase();

const getGenericGuide = (page) => {
  const phase = String(page?.phase || "").trim().toLowerCase();

  if (phase === "prelude") {
    return {
      eyebrow: "PROFESSOR SWAN · MISSION GUIDE",
      title: "Ready to become the engineer?",
      say:
        "I’ll guide you through the mission, but the ideas, observations, and final answers stay yours.",
      do_now:
        "Start with the mission story. Do not rush ahead to a bridge design—first understand the problem and the rules.",
      help_prompt:
        "Professor Swan, help me understand what I should do first without giving away the bridge solution.",
    };
  }

  if (phase === "wonder") {
    return {
      eyebrow: "PROFESSOR SWAN · WONDER",
      title: "Make a prediction before you test",
      say:
        "Scientists and engineers write down what they expect before they know the result. That makes the evidence meaningful later.",
      do_now:
        "Make your own prediction and give one reason. It is completely okay if the test proves you wrong.",
      help_prompt:
        "Professor Swan, help me make a prediction without telling me what will happen.",
    };
  }

  if (phase === "explore") {
    return {
      eyebrow: "PROFESSOR SWAN · EXPLORE",
      title: "Test carefully and watch what really happens",
      say:
        "For this part, your job is to collect evidence—not rescue the bridge or make the result look better.",
      do_now:
        "Follow the setup, run the test, and record what you actually observe.",
      help_prompt:
        "Professor Swan, help me run this test safely and fairly without telling me the result.",
    };
  }

  if (phase === "understand") {
    return {
      eyebrow: "PROFESSOR SWAN · UNDERSTAND",
      title: "Now connect your evidence to the science",
      say:
        "You already have real evidence from your own bridge. Now we can use models, videos, and questions to explain what was happening.",
      do_now:
        "Keep your Bridge 1 result in mind while you learn the science. Then explain it in your own words.",
      help_prompt:
        "Professor Swan, help me connect my bridge evidence to the science without designing Bridge 2 for me.",
    };
  }

  if (phase === "apply") {
    return {
      eyebrow: "PROFESSOR SWAN · APPLY",
      title: "Use the science to make one design decision",
      say:
        "There is no secret recipe to copy. Pick one change you think could help and explain why before you build.",
      do_now:
        "Describe your design change and prediction first. Then build it.",
      help_prompt:
        "Professor Swan, give me the smallest possible hint so I can make my own bridge design decision.",
    };
  }

  if (phase === "test & improve") {
    return {
      eyebrow: "PROFESSOR SWAN · TEST & IMPROVE",
      title: "A fair test makes your evidence trustworthy",
      say:
        "Bridge 2 does not have to win. What matters is testing it fairly and telling the truth about what happened.",
      do_now:
        "Keep the important test conditions the same, record Bridge 2, and compare it with Bridge 1.",
      help_prompt:
        "Professor Swan, help me check whether my Bridge 1 and Bridge 2 comparison is fair.",
    };
  }

  if (phase === "explain") {
    return {
      eyebrow: "PROFESSOR SWAN · EXPLAIN",
      title: "Make the evidence tell the engineering story",
      say:
        "A strong explanation connects what you observed, what you changed, and what the science says.",
      do_now:
        "Use your saved results. Do not replace your evidence with what you think should have happened.",
      help_prompt:
        "Professor Swan, challenge my explanation and ask me for evidence instead of writing it for me.",
    };
  }

  if (phase === "reflect") {
    return {
      eyebrow: "PROFESSOR SWAN · REFLECT",
      title: "Good evidence can change your mind",
      say:
        "Engineers learn from surprising results, weak designs, and failures—not only from the best score.",
      do_now:
        "Look back at your first prediction and name the evidence that changed or strengthened your thinking.",
      help_prompt:
        "Professor Swan, ask me what evidence changed my thinking without writing my reflection for me.",
    };
  }

  return {
    eyebrow: "PROFESSOR SWAN · GROW",
    title: "Turn what you learned into a new question",
    say:
      "Finishing one engineering cycle should create the next question. Choose one variable you are curious about.",
    do_now:
      "Plan what you would change, what you would keep the same, and what you would measure next time.",
    help_prompt:
      "Professor Swan, help me turn my next idea into a fair testable question.",
  };
};

export default function ProfessorSwanGuide({
  page,
  stepIndex = 0,
  stepCount = 0,
  onAsk,
  autoReadEnabled = false,
  language = "en",
  isDarkMode = false,
  t = (value) => value,
}) {
  if (!page) return null;

  const authored = page?.self_guided || {};
  const guide = { ...getGenericGuide(page), ...authored };
  const readAloud = useReadAloud({ language });
  const narratedKeyRef = useRef("");

  useEffect(() => {
    if (!autoReadEnabled || !readAloud.supported) return;

    const text = String(guide?.do_now || "").trim();
    if (!text) return;

    const key = `${stepIndex}:${text}`;
    if (narratedKeyRef.current === key) return;
    narratedKeyRef.current = key;

    // Narrate only the short action instruction, not the entire card.
    readAloud.speak(text);
  }, [
    autoReadEnabled,
    guide?.do_now,
    readAloud.speak,
    readAloud.supported,
    stepIndex,
  ]);

  const flowModes = getInstructionalFlowModes(page?.instructional_flow)
    .map((mode) => MODE_COPY[normalizeMode(mode?.value)])
    .filter(Boolean);

  const materials = Array.isArray(guide?.materials) ? guide.materials : [];
  const safety = Array.isArray(guide?.safety) ? guide.safety : [];
  const parentNote = String(guide?.parent_note || "").trim();

  const c = {
    panel: isDarkMode ? "#0b172a" : "#f0f9ff",
    border: isDarkMode ? "rgba(56,189,248,0.34)" : "#bae6fd",
    text: isDarkMode ? "#f8fafc" : "#0f172a",
    muted: isDarkMode ? "#cbd5e1" : "#475569",
    accent: isDarkMode ? "#7dd3fc" : "#0369a1",
    chip: isDarkMode ? "rgba(14,165,233,0.12)" : "#ffffff",
    parent: isDarkMode ? "rgba(245,158,11,0.12)" : "#fffbeb",
    parentBorder: isDarkMode ? "rgba(251,191,36,0.30)" : "#fde68a",
  };

  return (
    <View
      style={{
        marginBottom: 22,
        borderRadius: 18,
        borderWidth: 1,
        borderColor: c.border,
        backgroundColor: c.panel,
        overflow: "hidden",
      }}
    >
      <View
        style={{
          flexDirection: "row",
          alignItems: "stretch",
          gap: 18,
          padding: 18,
          flexWrap: "wrap",
        }}
      >
        <View
          style={{
            width: 150,
            minWidth: 150,
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: isDarkMode ? "rgba(255,255,255,0.04)" : "#ffffff",
            borderRadius: 14,
            padding: 8,
          }}
        >
          <Image
            source={professorSwanImage}
            resizeMode="contain"
            accessibilityLabel={t("Professor Swan, your AI STEM guide")}
            style={{ width: 134, height: 168 }}
          />
        </View>

        <View style={{ flex: 1, minWidth: 280 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 10,
              flexWrap: "wrap",
            }}
          >
            <Text
              style={{
                color: c.accent,
                fontSize: 12,
                fontWeight: "900",
                letterSpacing: 0.9,
              }}
            >
              {t(guide.eyebrow || "PROFESSOR SWAN")}
            </Text>
            {!!stepCount && (
              <Text style={{ color: c.muted, fontSize: 12, fontWeight: "800" }}>
                {t("Mission step")} {stepIndex + 1} / {stepCount}
              </Text>
            )}
          </View>

          <Text
            style={{
              color: c.text,
              fontSize: 22,
              lineHeight: 28,
              fontWeight: "900",
              marginTop: 6,
            }}
          >
            {t(guide.title || "Let's work through this together")}
          </Text>

          <Text
            style={{
              color: c.text,
              fontSize: 15,
              lineHeight: 23,
              marginTop: 9,
            }}
          >
            {t(guide.say || "I'll guide you, but the thinking and evidence stay yours.")}
          </Text>

          {!!guide.do_now && (
            <View
              style={{
                marginTop: 12,
                padding: 12,
                borderRadius: 12,
                backgroundColor: c.chip,
                borderWidth: 1,
                borderColor: c.border,
              }}
            >
              <Text style={{ color: c.accent, fontSize: 12, fontWeight: "900" }}>
                {t("DO THIS NOW")}
              </Text>
              <Text style={{ color: c.text, fontSize: 15, lineHeight: 22, marginTop: 4 }}>
                {t(guide.do_now)}
              </Text>
            </View>
          )}

          {flowModes.length > 0 && (
            <View style={{ marginTop: 12, gap: 7 }}>
              {flowModes.map((mode) => (
                <View
                  key={mode.label}
                  style={{
                    flexDirection: "row",
                    alignItems: "flex-start",
                    gap: 9,
                  }}
                >
                  <Text
                    style={{
                      width: 54,
                      color: c.accent,
                      fontSize: 11,
                      lineHeight: 18,
                      fontWeight: "900",
                    }}
                  >
                    {mode.label}
                  </Text>
                  <Text style={{ flex: 1, color: c.muted, fontSize: 13, lineHeight: 19 }}>
                    <Text style={{ color: c.text, fontWeight: "800" }}>{t(mode.title)}. </Text>
                    {t(mode.body)}
                  </Text>
                </View>
              ))}
            </View>
          )}

          <TouchableOpacity
            onPress={() => onAsk?.(guide.help_prompt)}
            accessibilityLabel={t("Ask Professor Swan for help")}
            style={{
              alignSelf: "flex-start",
              marginTop: 14,
              paddingHorizontal: 14,
              paddingVertical: 10,
              borderRadius: 10,
              backgroundColor: c.accent,
            }}
          >
            <Text style={{ color: "#ffffff", fontWeight: "900" }}>
              {"🦢"} {t("Ask Professor Swan for a hint")}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {(materials.length > 0 || safety.length > 0 || parentNote) && (
        <View
          style={{
            paddingHorizontal: 18,
            paddingVertical: 15,
            borderTopWidth: 1,
            borderTopColor: c.parentBorder,
            backgroundColor: c.parent,
          }}
        >
          <Text style={{ color: c.text, fontSize: 15, fontWeight: "900" }}>
            {"👨‍👩‍👧"} {t("Grown-up setup")}
          </Text>
          {!!parentNote && (
            <Text style={{ color: c.text, fontSize: 14, lineHeight: 21, marginTop: 6 }}>
              {t(parentNote)}
            </Text>
          )}

          {materials.length > 0 && (
            <View style={{ marginTop: 9 }}>
              <Text style={{ color: c.accent, fontSize: 12, fontWeight: "900" }}>
                {t("MATERIALS")}
              </Text>
              {materials.map((item) => (
                <Text key={item} style={{ color: c.text, fontSize: 14, lineHeight: 21, marginTop: 3 }}>
                  {"•"} {t(item)}
                </Text>
              ))}
            </View>
          )}

          {safety.length > 0 && (
            <View style={{ marginTop: 9 }}>
              <Text style={{ color: c.accent, fontSize: 12, fontWeight: "900" }}>
                {t("SAFETY")}
              </Text>
              {safety.map((item) => (
                <Text key={item} style={{ color: c.text, fontSize: 14, lineHeight: 21, marginTop: 3 }}>
                  {"•"} {t(item)}
                </Text>
              ))}
            </View>
          )}
        </View>
      )}
    </View>
  );
}
